import { ReactNode, TextareaHTMLAttributes, forwardRef, useId } from "react";
import styles from "./Textarea.module.css";

export type HelperVariant = "default" | "warn" | "error" | "ok";

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  hintRight?: ReactNode;
  modified?: boolean;
  helperVariant?: HelperVariant;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      label,
      hint,
      error,
      hintRight,
      modified,
      helperVariant = "default",
      id,
      rows = 4,
      className = "",
      ...rest
    },
    ref,
  ) {
    const reactId = useId();
    const fid = id ?? reactId;

    const helperClass =
      helperVariant === "warn"
        ? styles.helperWarn
        : helperVariant === "error"
          ? styles.helperError
          : helperVariant === "ok"
            ? styles.helperOk
            : styles.helper;

    const taClass = [
      styles.textarea,
      error ? styles.error : "",
      !error && modified ? styles.modified : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={styles.wrap}>
        {(label || hintRight) && (
          <div className={hintRight ? styles.labelRow : undefined}>
            {label && (
              <label className={styles.label} htmlFor={fid}>
                {label}
              </label>
            )}
            {hintRight && (
              <span className={styles.hintRight}>{hintRight}</span>
            )}
          </div>
        )}
        <textarea
          ref={ref}
          id={fid}
          rows={rows}
          className={taClass}
          aria-invalid={!!error || undefined}
          aria-describedby={
            error ? `${fid}-err` : hint ? `${fid}-hint` : undefined
          }
          {...rest}
        />
        {error && (
          <div id={`${fid}-err`} className={styles.errorText}>
            {error}
          </div>
        )}
        {!error && hint && (
          <div id={`${fid}-hint`} className={helperClass}>
            {hint}
          </div>
        )}
      </div>
    );
  },
);
